import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import type { InferResponseType } from 'hono/client';
import { ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplicationCard } from './application-card';

import { Badge } from '~/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import type { api } from '~/lib/api';

type ApplicationsResponse = InferResponseType<
  (typeof api.api.org)[':orgId']['department'][':deptId']['jobs'][':jobId']['applications']['$get']
>;
type ApplicationItem = ApplicationsResponse['applications'][number];
type StageItem = ApplicationsResponse['stages'][number];

const LOCKED_SYSTEM_KEYS = new Set(['invited', 'did_not_finish', 'failed_screening_interview']);

type ApplicationKanbanProps = {
  applications: ApplicationItem[];
  stages: StageItem[];
  scoreMap: Map<string, number>;
  onApplicationClick: (application: ApplicationItem) => void;
  onStageChange?: (applicationId: string, stageId: string) => void;
};

function DroppableColumn({
  stage,
  count,
  children
}: {
  stage: StageItem;
  count: number;
  children: React.ReactNode;
}): React.ReactNode {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });

  return (
    <div ref={setNodeRef} className="flex w-full flex-shrink-0 flex-col sm:w-72 sm:min-h-0">
      <div className="mb-3 flex items-center gap-2">
        {stage.color && (
          <div className="size-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
        )}
        <h3 className="text-sm font-semibold">{stage.name}</h3>
        <span className="text-xs text-muted-foreground">{count}</span>
        {stage.systemKey === 'passed_screening_interview' && (
          <Badge className="ml-auto border-0 bg-blue-100 text-xs text-blue-700">
            {t('applications.aiRanked')}
          </Badge>
        )}
      </div>
      <div
        className={`flex min-h-[80px] flex-col gap-2 rounded-lg border border-dashed p-2 transition-colors sm:flex-1 sm:min-h-0 sm:overflow-y-auto ${
          isOver ? 'border-primary bg-primary/5' : 'border-transparent'
        }`}
      >
        {children}
        {!children ||
          (Array.isArray(children) && (children as React.ReactNode[]).length === 0 && (
            <p className="py-4 text-center text-xs text-muted-foreground">
              {t('applications.emptyStage')}
            </p>
          ))}
      </div>
    </div>
  );
}

function DraggableCard({
  application,
  score,
  onClick
}: {
  application: ApplicationItem;
  score?: number;
  onClick: () => void;
}): React.ReactNode {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: application.id
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`cursor-grab touch-none ${isDragging ? 'opacity-30' : ''}`}
    >
      <ApplicationCard application={application} score={score} onClick={onClick} />
    </div>
  );
}

function ScreeningSubSection({
  stage,
  applications,
  scoreMap,
  defaultOpen,
  onApplicationClick
}: {
  stage: StageItem;
  applications: ApplicationItem[];
  scoreMap: Map<string, number>;
  defaultOpen: boolean;
  onApplicationClick: (app: ApplicationItem) => void;
}): React.ReactNode {
  return (
    <Collapsible defaultOpen={defaultOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent">
        {stage.color && (
          <div className="size-2 rounded-full" style={{ backgroundColor: stage.color }} />
        )}
        <span className="font-medium text-muted-foreground">{stage.name}</span>
        <span className="ml-auto text-xs text-muted-foreground">{applications.length}</span>
        <ChevronRight className="size-4 text-muted-foreground transition-transform [[data-panel-open]_&]:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-2 pt-2">
          {applications.length === 0 ? (
            <p className="py-2 text-center text-xs text-muted-foreground">—</p>
          ) : (
            applications.map((app) => (
              <ApplicationCard
                key={app.id}
                application={app}
                score={scoreMap.get(app.id)}
                onClick={() => {
                  onApplicationClick(app);
                }}
              />
            ))
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ScreeningColumn({
  stages,
  grouped,
  scoreMap,
  onApplicationClick
}: {
  stages: StageItem[];
  grouped: Map<string, ApplicationItem[]>;
  scoreMap: Map<string, number>;
  onApplicationClick: (app: ApplicationItem) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  const invited = stages.find((s) => s.systemKey === 'invited');
  const dnf = stages.find((s) => s.systemKey === 'did_not_finish');
  const failed = stages.find((s) => s.systemKey === 'failed_screening_interview');

  const totalCount = stages.reduce((sum, s) => sum + (grouped.get(s.id)?.length ?? 0), 0);

  return (
    <div className="flex w-full flex-shrink-0 flex-col sm:w-72 sm:min-h-0">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-semibold">{t('applications.screening')}</h3>
        <span className="text-xs text-muted-foreground">{totalCount}</span>
      </div>
      <div className="flex flex-col gap-1 rounded-lg border border-dashed border-transparent p-2 sm:flex-1 sm:min-h-0 sm:overflow-y-auto">
        {invited && (
          <ScreeningSubSection
            stage={invited}
            applications={grouped.get(invited.id) ?? []}
            scoreMap={scoreMap}
            defaultOpen={true}
            onApplicationClick={onApplicationClick}
          />
        )}
        {dnf && (
          <ScreeningSubSection
            stage={dnf}
            applications={grouped.get(dnf.id) ?? []}
            scoreMap={scoreMap}
            defaultOpen={false}
            onApplicationClick={onApplicationClick}
          />
        )}
        {failed && (
          <ScreeningSubSection
            stage={failed}
            applications={grouped.get(failed.id) ?? []}
            scoreMap={scoreMap}
            defaultOpen={false}
            onApplicationClick={onApplicationClick}
          />
        )}
      </div>
    </div>
  );
}

export function ApplicationKanban({
  applications,
  stages,
  scoreMap,
  onApplicationClick,
  onStageChange
}: ApplicationKanbanProps): React.ReactNode {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [stageOverrides, setStageOverrides] = useState<Map<string, string>>(new Map());

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Clear overrides once the parent data catches up
  useEffect(() => {
    if (stageOverrides.size === 0) return;
    const next = new Map<string, string>();
    for (const [appId, stageId] of stageOverrides) {
      const app = applications.find((a) => a.id === appId);
      if (app && app.stageId !== stageId) {
        next.set(appId, stageId);
      }
    }
    if (next.size !== stageOverrides.size) {
      setStageOverrides(next);
    }
  }, [applications, stageOverrides]);

  const lockedStages = useMemo(
    () => stages.filter((s) => LOCKED_SYSTEM_KEYS.has(s.systemKey ?? '')),
    [stages]
  );
  const pipelineStages = useMemo(
    () => stages.filter((s) => !LOCKED_SYSTEM_KEYS.has(s.systemKey ?? '')),
    [stages]
  );
  const lockedStageIds = useMemo(() => new Set(lockedStages.map((s) => s.id)), [lockedStages]);

  const grouped = useMemo(() => {
    const map = new Map<string, ApplicationItem[]>();
    for (const stage of stages) {
      map.set(stage.id, []);
    }
    for (const app of applications) {
      const effectiveStageId = stageOverrides.get(app.id) ?? app.stageId;
      const list = map.get(effectiveStageId);
      if (list) list.push(app);
    }

    // Sort "Passed Screening" by score descending
    const passedStage = stages.find((s) => s.systemKey === 'passed_screening_interview');
    if (passedStage) {
      const list = map.get(passedStage.id);
      if (list) {
        list.sort((a, b) => {
          const sa = scoreMap.get(a.id) ?? -1;
          const sb = scoreMap.get(b.id) ?? -1;
          return sb - sa;
        });
      }
    }

    return map;
  }, [applications, stages, stageOverrides, scoreMap]);

  const activeApplication = useMemo(
    () => (activeId ? (applications.find((a) => a.id === activeId) ?? null) : null),
    [activeId, applications]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over || !onStageChange) return;

      const applicationId = active.id as string;
      const newStageId = over.id as string;

      // Prevent drops on locked stages
      if (lockedStageIds.has(newStageId)) return;

      const app = applications.find((a) => a.id === applicationId);
      if (!app || app.stageId === newStageId) return;

      setStageOverrides((prev) => new Map(prev).set(applicationId, newStageId));
      onStageChange(applicationId, newStageId);
    },
    [applications, onStageChange, lockedStageIds]
  );

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex flex-col gap-4 pb-4 sm:flex-row sm:flex-1 sm:min-h-0 sm:overflow-x-auto">
        {lockedStages.length > 0 && (
          <ScreeningColumn
            stages={lockedStages}
            grouped={grouped}
            scoreMap={scoreMap}
            onApplicationClick={onApplicationClick}
          />
        )}
        {pipelineStages.map((stage) => {
          const stageApps = grouped.get(stage.id) ?? [];
          return (
            <DroppableColumn key={stage.id} stage={stage} count={stageApps.length}>
              {stageApps.length === 0 ? (
                <p className="py-4 text-center text-xs text-muted-foreground">
                  {t('applications.emptyStage')}
                </p>
              ) : (
                stageApps.map((app) => (
                  <DraggableCard
                    key={app.id}
                    application={app}
                    score={scoreMap.get(app.id)}
                    onClick={() => {
                      onApplicationClick(app);
                    }}
                  />
                ))
              )}
            </DroppableColumn>
          );
        })}
      </div>
      <DragOverlay>
        {activeApplication && (
          <div className="w-72 rotate-2 opacity-90 shadow-lg">
            <ApplicationCard
              application={activeApplication}
              score={scoreMap.get(activeApplication.id)}
              onClick={() => {}}
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
